import type { MarkerTarget, MarkerView, PlotInfo } from "../net/api";
import { hint } from "../state/touch";

/** Spec 2h §3.2: a plot holds at most this many markers, whatever their status. */
export const MAX_MARKERS = 50;
/** Keeps local x and z strictly inside the half-open footprint after rounding (Review Focus 3). */
const EDGE = 0.001;

type Vec = { x: number; y: number; z: number };
type Height = (x: number, z: number) => number;

const round3 = (v: number) => Math.round(v * 1000) / 1000;
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** A world point as the plot-local [x, y, z] the server stores (spec 2h §3.1): y is above the ground there. */
export function toLocal(plot: PlotInfo, p: Vec, height: Height): [number, number, number] {
  const w = plot.bounds.max[0] - plot.bounds.min[0], d = plot.bounds.max[1] - plot.bounds.min[1];
  return [
    clamp(round3(p.x - plot.bounds.min[0]), 0, w - EDGE),
    clamp(round3(p.y - height(p.x, p.z)), -2, plot.height),
    clamp(round3(p.z - plot.bounds.min[1]), 0, d - EDGE),
  ];
}

export function toWorld(plot: PlotInfo, pos: [number, number, number], height: Height): Vec {
  const x = plot.bounds.min[0] + pos[0], z = plot.bounds.min[1] + pos[2];
  return { x, y: height(x, z) + pos[1], z };
}

/** The first n characters (code points), with "…" when cut. */
export function clip(s: string, n: number): string {
  const cs = [...s];
  return cs.length > n ? cs.slice(0, n).join("") + "…" : s;
}

/** "標在：…" names the target like the look-at line (rules.ts entityLabel); "標在地面" without one. */
export function targetLabel(t: Partial<MarkerTarget> | null): string {
  return t ? `標在：${t.name || t.asset_code || t.role || "幾何體"}` : "標在地面";
}

export interface MarkerHit { point: Vec; entity: { id: string; name: string } | null }
export type MarkerAim =
  | { ok: false; prompt: string }
  | { ok: true; prompt: string; plot: PlotInfo; point: Vec; entity: { id: string; name: string } | null };

/** What marker mode does with what is under the crosshair (spec 2h §2.2). */
export function markerTarget(
  hit: MarkerHit | null, plots: PlotInfo[], roleIn: (plotId: string) => string,
  markers: Record<string, MarkerView[]>, failed: string[],
): MarkerAim {
  if (!hit) return { ok: false, prompt: "瞄準地面或物件" };
  const { x, z } = hit.point;
  const plot = plots.find((p) => x >= p.bounds.min[0] && x < p.bounds.max[0] && z >= p.bounds.min[1] && z < p.bounds.max[1]);
  if (!plot || plot.kind !== "estate" || !roleIn(plot.id)) return { ok: false, prompt: "只能標在你的領地內" };
  if (failed.includes(plot.id)) return { ok: false, prompt: "標記暫時無法載入" };
  const list = markers[plot.id];
  if (!list) return { ok: false, prompt: "標記載入中…" };
  if (list.length >= MAX_MARKERS) return { ok: false, prompt: `這塊領地的標記已滿（${MAX_MARKERS}）` };
  const prompt = hit.entity
    ? hint(`左鍵標記「${hit.entity.name}」 · Esc 取消`, `點 ✓ 標記「${hit.entity.name}」`)
    : hint("左鍵在此標記 · Esc 取消", "點 ✓ 在此標記");
  return { ok: true, prompt, plot, point: hit.point, entity: hit.entity };
}
